import { useState, type FormEvent } from 'react';
import {
  importMailcomAliases,
  listMailcomAliases,
  type MailcomImportAliasesResponse,
  type WireProviderAlias,
} from '../../api/endpoints.js';
import { useApiMutation, useApiQuery } from '../../api/hooks.js';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Modal,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
  Textarea,
} from '../../ui/index.js';
import { ALIAS_STATUS_LABEL, fmtDate, fmtDateOnly, toneForAlias } from './shared.js';

export interface AliasListModalProps {
  readonly providerId: string;
  readonly open: boolean;
  readonly accountId: string | null;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

export function AliasListModal({ providerId, open, accountId, canManage, onClose }: AliasListModalProps): JSX.Element | null {
  if (!open || !accountId) return null;
  return (
    <Modal open={open} onClose={onClose} title="Alias Yönetimi" width={780} testId="alias-list-modal">
      <AliasListBody providerId={providerId} accountId={accountId} canManage={canManage} />
    </Modal>
  );
}

function AliasListBody({ providerId, accountId, canManage }: { readonly providerId: string; readonly accountId: string; readonly canManage: boolean }): JSX.Element {
  const aliasesQuery = useApiQuery<{ total: number; aliases: readonly WireProviderAlias[] }>(
    (o) => listMailcomAliases(o, accountId, providerId),
    [accountId, providerId],
  );
  const [showImport, setShowImport] = useState(false);

  return (
    <div>
      <div
        className="mcp-modal-toolbar"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}
      >
        <span className="t-tertiary t-small mcp-modal-meta">
          Account ID: <code className="t-mono">{accountId.slice(0, 8)}…</code>
          {aliasesQuery.kind === 'ok' ? ` · Toplam ${aliasesQuery.data?.total ?? 0}` : ''}
        </span>
        <div className="mcp-modal-actions" style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={() => aliasesQuery.reload()} icon={<Icon.Refresh size={12} />}>
            Yenile
          </Button>
          {canManage ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowImport((v) => !v)}
              icon={<Icon.Plus size={12} />}
            >
              {showImport ? 'İptal' : 'Alias içe aktar'}
            </Button>
          ) : null}
        </div>
      </div>

      {showImport && canManage ? (
        <div style={{ marginBottom: 16 }}>
          <ImportForm
            providerId={providerId}
            accountId={accountId}
            onSuccess={() => {
              setShowImport(false);
              aliasesQuery.reload();
            }}
          />
        </div>
      ) : null}

      {aliasesQuery.kind === 'loading' ? <LoadingState /> : null}
      {aliasesQuery.kind === 'error' ? <ErrorState reason={aliasesQuery.reason} onRetry={aliasesQuery.reload} /> : null}
      {aliasesQuery.kind === 'ok' ? (
        (aliasesQuery.data?.aliases ?? []).length === 0 ? (
          <EmptyState
            label="Bu parent için alias henüz içe aktarılmamış."
            hint="İçe aktar butonu ile alias listesini ekleyin."
            icon={<Icon.Mail size={22} />}
          />
        ) : (
          <Table dense stack className="mcp-alias-table">
            <THead>
              <TRow>
                <TH>Domain</TH>
                <TH>Durum</TH>
                <TH>Sunum</TH>
                <TH>Son kullanım</TH>
                <TH>Eklendi</TH>
              </TRow>
            </THead>
            <TBody>
              {(aliasesQuery.data?.aliases ?? []).map((a) => (
                <TRow key={a.id}>
                  <TD className="t-mono tbl-stack-primary" label="Domain">{a.domain}</TD>
                  <TD className="tbl-stack-status" label="Durum">
                    <Badge tone={toneForAlias(a.status)} dot>
                      {ALIAS_STATUS_LABEL[a.status] ?? a.status}
                    </Badge>
                  </TD>
                  <TD className="t-mono t-num" label="Sunum">{a.servedCount}</TD>
                  <TD className="t-mono t-xs t-tertiary" label="Son kullanım">{fmtDate(a.lastServedAt)}</TD>
                  <TD className="t-mono t-xs t-tertiary" label="Eklendi">{fmtDateOnly(a.createdAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )
      ) : null}
    </div>
  );
}

/**
 * "n alias içe aktarıldı" plus what was skipped; D-240: an address already
 * served to an order is never imported again (`previouslyServed` ⊆ skipped).
 */
export function importResultText(data: MailcomImportAliasesResponse): string {
  const parts = [`${data.imported} alias içe aktarıldı`];
  const skipped = typeof data.skipped === 'number' ? data.skipped : 0;
  const served = typeof data.previouslyServed === 'number' ? Math.min(data.previouslyServed, skipped) : 0;
  if (skipped > 0) {
    parts.push(
      served > 0
        ? `${skipped} atlandı (${served} tanesi daha önce bir siparişe verildiği için bir daha eklenemez)`
        : `${skipped} atlandı`,
    );
  }
  return `${parts.join(', ')}.`;
}

function ImportForm({ providerId, accountId, onSuccess }: { readonly providerId: string; readonly accountId: string; readonly onSuccess: () => void }): JSX.Element {
  const [raw, setRaw] = useState('');
  const mutation = useApiMutation((o, input: { accountId: string; addresses: string[] }) =>
    importMailcomAliases(o, input.accountId, input.addresses, providerId),
  );

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    const addresses = raw
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (addresses.length === 0) return;
    const result = await mutation.run({ accountId, addresses });
    if (result.kind === 'ok') {
      setRaw('');
      onSuccess();
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ padding: 14, background: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}
      aria-label="alias içe aktar"
    >
      <h3 style={{ margin: '0 0 6px', fontSize: 13.5, fontWeight: 600 }}>Alias İçe Aktar</h3>
      <p className="t-tertiary t-xs" style={{ margin: '0 0 10px' }}>
        Her satırda bir alias. Virgül veya noktalı virgülle ayrılmış format da kabul edilir. Var olan adresler ve daha
        önce bir siparişe verilmiş adresler (D-240) atlanır.
      </p>
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder={'alias1@mail.com\nalias2@email.com'}
        rows={5}
        required
      />
      <div className="mcp-form-actions mcp-form-actions-start" style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <Button type="submit" variant="primary" size="sm" loading={mutation.state.kind === 'running'}>
          İçe aktar
        </Button>
      </div>
      {mutation.state.kind === 'error' ? (
        <div style={{ marginTop: 8 }}>
          <Banner tone="danger">Hata: {mutation.state.reason}</Banner>
        </div>
      ) : null}
      {mutation.state.kind === 'ok' ? (
        <div style={{ marginTop: 8 }} data-testid="alias-import-result">
          <Banner tone="success">{importResultText(mutation.state.data)}</Banner>
        </div>
      ) : null}
    </form>
  );
}
